import { useWindowWidth } from '@/hooks/useWindowWidth'
import { Tooltip, TooltipProps } from '@gate/gui'
import { useClickAway, useInViewport, useMemoizedFn, useScroll } from 'ahooks'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

export interface BaseTooltipProps extends TooltipProps {
  children: ReactNode
  component?: 'a' | 'div' | 'span' | 'p' | 'h1' | 'h2'
  childStyle?: React.CSSProperties
  isToolTipState?: boolean
  tooltipMaw?: number
  ellipsisLines?: number // 新增属性，用于指定省略的行数
  tooltipLine?: number
}

// 修改 BaseTooltipStyled 组件，根据 ellipsisLines 属性动态设置样式
const BaseTooltipStyled = styled.div<{ ellipsisLines?: number }>`
  max-width: max-content;
  width: 100%;
  ${({ ellipsisLines }) =>
    ellipsisLines
      ? `
    display: -webkit-box;
    -webkit-line-clamp: ${ellipsisLines};
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
  `
      : `
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`

const BaseTooltipLabelStyled = styled.div<{ tooltipLine?: number }>`
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: ${({ tooltipLine }) => (tooltipLine ? tooltipLine : 5)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5; /* 根据实际需要调整行高 */
  max-height: ${({ tooltipLine }) =>
    tooltipLine ? `calc(1.5em * ${tooltipLine})` : 'calc(1.5em * 5)'}; /* 根据行高调整最大高度 */
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-6);
  word-break: break-all;
  white-space: pre-wrap;
`

/**
 * 自动省略后出现Tooltip
 * @param param0
 * @returns
 */
const BaseTooltip: FC<BaseTooltipProps> = ({
  isToolTipState,
  children,
  className,
  childStyle,
  component = 'span',
  label,
  styles,
  tooltipMaw = 240,
  zIndex,
  ellipsisLines,
  tooltipLine,
  ...props
}) => {
  const ref = useRef<any>()
  const [flag, setFlag] = useState(false)

  const [opened, setOpened] = useState<boolean>(false)
  const scroll = useScroll()

  useEffect(() => {
    if (scroll && scroll.top > 200) {
      if (opened) {
        setOpened(false)
      }
    }
    // !不难添加 opened 的监听
  }, [scroll])

  useClickAway(() => {
    setOpened(false)
  }, ref)

  const observe = useMemoizedFn(() => {
    setFlag(
      ref.current.offsetWidth < ref.current.scrollWidth ||
        (Boolean(ellipsisLines) && ref.current.offsetHeight < ref.current.scrollHeight)
    )
  })

  const windowWidth = useWindowWidth()

  const [inViewport] = useInViewport(ref)

  useEffect(() => {
    if (inViewport && windowWidth) {
      observe()
    }
  }, [observe, windowWidth, inViewport])

  return (
    <>
      {flag || isToolTipState ? (
        <Tooltip
          opened={opened}
          withinPortal
          events={{ focus: true, hover: true, touch: true }}
          {...props}
          label={<BaseTooltipLabelStyled tooltipLine={tooltipLine}>{label}</BaseTooltipLabelStyled>}
          styles={{
            ...styles,
            tooltip: {
              maxWidth: tooltipMaw + 'px',
              ...((styles as any)?.tooltip ?? {}),
              wordWrap: 'break-word',
              zIndex: zIndex ?? '14 !important',
            } as any,
          }}
        >
          <BaseTooltipStyled
            style={{ cursor: 'pointer', ...childStyle }}
            className={className}
            as={component}
            ref={ref}
            onClick={() => setOpened(() => true)}
            onMouseEnter={() => setOpened(() => true)}
            onMouseLeave={() => setOpened(false)}
            ellipsisLines={ellipsisLines} // 传递 ellipsisLines 属性
            {...props}
          >
            {children}
          </BaseTooltipStyled>
        </Tooltip>
      ) : (
        <BaseTooltipStyled
          {...props}
          style={{ ...childStyle }}
          className={className}
          as={component}
          ref={ref}
          ellipsisLines={ellipsisLines} // 传递 ellipsisLines 属性
        >
          {children}
        </BaseTooltipStyled>
      )}
    </>
  )
}

export default BaseTooltip
